<template>
  <div class="sidebar-user-card">
    <div
      v-if="showCompanySelect"
      class="sidebar-company"
      v-click-outside="closeCompanyMenu"
    >
      <button
        type="button"
        class="sidebar-company-toggle"
        :class="{ open: showCompanyMenu }"
        @click="showCompanyMenu = !showCompanyMenu"
      >
        <span class="sidebar-company-dot"></span>
        <span class="sidebar-company-name">{{ activeCompanyName }}</span>
        <i class="ti ti-chevron-down"></i>
      </button>
      <ul v-if="showCompanyMenu" class="sidebar-company-menu">
        <li
          v-for="company in userCompanies"
          :key="company.id"
          class="sidebar-company-option"
          :class="{ active: company.id === activeCompany }"
          @click="selectCompany(company.id)"
        >
          {{ company.name }}
        </li>
      </ul>
    </div>

    <div class="sidebar-user-row" v-click-outside="closeUserMenu">
      <div class="sidebar-user-main">
        <div class="sidebar-user-avatar">
          <i class="ti ti-user"></i>
        </div>
        <div class="sidebar-user-info">
          <div class="sidebar-user-name">{{ userName }}</div>
        </div>
      </div>
      <div class="sidebar-user-actions">
        <button
          type="button"
          class="sidebar-settings-btn"
          title="Настройки"
          @click="showUserMenu = !showUserMenu"
        >
          <i class="ti ti-settings"></i>
        </button>
        <ul v-if="showUserMenu" class="sidebar-user-menu">
          <li>
            <a href="#" @click.prevent="openSettings">
              <i class="ti ti-settings"></i>
              Настройки
            </a>
          </li>
          <li v-if="billingEnabled">
            <RouterLink :to="{name: 'billing'}" @click="showUserMenu = false">
              <i class="ti ti-credit-card"></i>
              Тариф и оплата
            </RouterLink>
          </li>
          <li>
            <a href="javascript:void(0);" @click.prevent="logout">
              <i class="ti ti-logout"></i>
              Выйти
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Новый биллинг: тариф видит владелец компании (у остальных кабинет оплаты закрыт) -->
    <RouterLink v-if="billingEnabled && billingTariff" :to="{name: 'billing'}" class="sidebar-tariff">
      <div class="sidebar-tariff-row">
        <span class="sidebar-tariff-label">Тариф</span>
        <span class="badge" :class="billingTariff.active ? 'bg-primary' : 'bg-secondary'">{{ billingTariff.name }}</span>
      </div>
      <div class="sidebar-tariff-status" :class="'text-' + (billingTariff.tone === 'muted' ? 'muted' : billingTariff.tone)">
        {{ billingTariff.note }}
        <RouterLink v-if="billingTariff.action" :to="billingTariff.action.to" class="sidebar-tariff-link" @click.stop>
          {{ billingTariff.action.text }}
        </RouterLink>
      </div>
    </RouterLink>

    <RouterLink v-else-if="!billingEnabled" :to="tariffLink" class="sidebar-tariff">
      <div class="sidebar-tariff-row">
        <span class="sidebar-tariff-label">Тариф</span>
        <span class="badge bg-primary">{{ tariffName }}</span>
      </div>
      <div v-if="!isActive" class="sidebar-tariff-status text-danger">
        Тариф неактивен
        <RouterLink :to="renewLink" class="sidebar-tariff-link" @click.stop>Продлить</RouterLink>
      </div>
      <div v-else-if="isExpiringSoon" class="sidebar-tariff-status text-warning">
        Скоро заканчивается
        <RouterLink :to="renewLink" class="sidebar-tariff-link" @click.stop>Продлить</RouterLink>
      </div>
      <div v-else-if="!isFreeTariff && tariffEndDate" class="sidebar-tariff-status text-muted">
        До: {{ formatDate(tariffEndDate) }}
      </div>
    </RouterLink>
  </div>
</template>

<script>
import {VOnClickOutside as ClickOutside} from "@/helpers/directives/clickOutside"
import {user} from "@/data/user";
import {auth} from "@/data/auth";
import {subscription} from "@/data/subscription";
import {billingEnabled} from "@/modules/billing";
import {describeTariff, tariffSummary} from "@/modules/billing/summary";

export default {
  name: "SidebarUser",
  directives: {
    ClickOutside
  },
  data() {
    return {
      showCompanyMenu: false,
      showUserMenu: false,
      activeCompany: '',
      userCompanies: [],
      isActive: false,
      isExpiringSoon: false,
    }
  },
  computed: {
    userName() {
      return user.name || ''
    },
    showCompanySelect() {
      return this.userCompanies.length > 1
    },
    activeCompanyName() {
      const current = this.userCompanies.find((item) => item.id === this.activeCompany)
      return current ? current.name : ''
    },
    subscriptionInfo() {
      return subscription.subscription_info
    },
    isFreeTariff() {
      if (!this.subscriptionInfo.has_active_subscription || !this.subscriptionInfo.subscription) {
        return false
      }
      const price = this.subscriptionInfo.subscription.price
      return price === 0 || price === null || price === undefined
    },
    tariffName() {
      if (this.subscriptionInfo.has_active_subscription && this.subscriptionInfo.subscription) {
        return this.subscriptionInfo.subscription.name || 'Не указан'
      }
      return 'Не активен'
    },
    tariffEndDate() {
      return this.subscriptionInfo.subscription && this.subscriptionInfo.subscription.end_date
    },
    billingEnabled() {
      return billingEnabled
    },
    // Тариф из биллинга; null — ещё не загружен или нет прав на кабинет оплаты
    billingTariff() {
      if (!tariffSummary.loaded || tariffSummary.forbidden || tariffSummary.error) {
        return null
      }
      return describeTariff(tariffSummary.subscription)
    },
    // Ссылки старого блока тарифа (без биллинга)
    tariffLink() {
      return '/settings'
    },
    renewLink() {
      return '/payment'
    }
  },
  methods: {
    closeCompanyMenu() {
      this.showCompanyMenu = false
    },
    closeUserMenu() {
      this.showUserMenu = false
    },
    openSettings() {
      this.showUserMenu = false
      this.$router.push({name: 'settings'})
    },
    async logout() {
      this.showUserMenu = false
      await auth.logout()
    },
    selectCompany(id) {
      this.showCompanyMenu = false
      if (id === this.activeCompany) {
        return
      }
      this.activeCompany = id
      user.setActiveCompany(id)
      setTimeout(() => location = window.location, 200)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    async updateSubscriptionStatus() {
      if (!auth.checkAuth()) {
        this.isActive = false
        this.isExpiringSoon = false
        return
      }
      this.isActive = await subscription.isActive()
      this.isExpiringSoon = await subscription.isExpiringSoon()
    }
  },
  watch: {
    subscriptionInfo: {
      async handler(newVal) {
        if (!auth.checkAuth() || !newVal || Object.keys(newVal).length === 0) {
          this.isActive = false
          this.isExpiringSoon = false
          return
        }
        await this.updateSubscriptionStatus()
      },
      deep: true
    }
  },
  async mounted() {
    await user.getUserBase()
    this.userCompanies = await user.getUserCompanies() || []
    this.activeCompany = await user.getActiveCompany()
    if (billingEnabled) {
      await tariffSummary.load()
      return
    }
    await subscription.getSubscriptionInfo()
    await this.updateSubscriptionStatus()
  }
}
</script>

<style scoped>
.sidebar-user-card {
  flex: 0 0 auto;
  margin: 0.75rem 1rem 0.5rem;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid rgba(47, 43, 61, 0.08);
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(47, 43, 61, 0.04);
}

.sidebar-company {
  position: relative;
  margin-bottom: 0.75rem;
}

.sidebar-company-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border: 1px solid rgba(47, 43, 61, 0.12);
  border-radius: 0.5rem;
  color: #2f2b3d;
  font-size: 0.9375rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.sidebar-company-toggle:hover {
  border-color: rgba(115, 103, 240, 0.45);
}

.sidebar-company-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7367f0;
}

.sidebar-company-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-company-toggle .ti {
  flex: 0 0 auto;
  font-size: 1rem;
  color: #a8a5b8;
  transition: transform 0.15s ease;
}

.sidebar-company-toggle.open .ti {
  transform: rotate(180deg);
}

.sidebar-company-menu,
.sidebar-user-menu {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 4px);
  z-index: 20;
  margin: 0;
  padding: 0.375rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid rgba(47, 43, 61, 0.08);
  border-radius: 0.5rem;
  box-shadow: 0 6px 16px rgba(47, 43, 61, 0.12);
  max-height: 16rem;
  overflow: auto;
}

.sidebar-company-option {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: #2f2b3d;
  font-size: 0.875rem;
}

.sidebar-company-option:hover,
.sidebar-company-option.active {
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
}

.sidebar-user-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sidebar-user-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
}

.sidebar-user-actions {
  position: relative;
  flex: 0 0 auto;
}

.sidebar-user-avatar {
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-user-avatar .ti {
  font-size: 1.25rem;
}

.sidebar-user-info {
  flex: 1 1 auto;
  min-width: 0;
}

.sidebar-user-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: #2f2b3d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-settings-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8d8a99;
  border-radius: 0.375rem;
  cursor: pointer;
}

.sidebar-settings-btn:hover,
.sidebar-settings-btn:focus {
  color: #7367f0;
  background: rgba(115, 103, 240, 0.08);
}

.sidebar-settings-btn .ti {
  font-size: 1.25rem;
}

.sidebar-user-menu {
  left: auto;
  right: 0;
  width: 10.5rem;
}

.sidebar-user-menu a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: #2f2b3d;
  font-size: 0.875rem;
}

.sidebar-user-menu a:hover {
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
}

.sidebar-user-menu .ti {
  font-size: 1rem;
}

.sidebar-tariff {
  display: block;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(47, 43, 61, 0.08);
  color: inherit;
  text-decoration: none;
}

.sidebar-tariff:hover {
  color: inherit;
}

.sidebar-tariff-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.sidebar-tariff-label {
  font-size: 0.8125rem;
  color: #8d8a99;
}

.sidebar-tariff-status {
  margin-top: 0.375rem;
  font-size: 0.75rem;
}

.sidebar-tariff-link {
  margin-left: 0.25rem;
  color: inherit;
  text-decoration: underline;
}

.sidebar-tariff-link:hover {
  opacity: 0.8;
}
</style>
